import type { LLMModel } from '@eureka/shared/select-llm';
import type { ISolution } from '@eureka/shared/solution-card/src/types';
import type { Instance } from 'tippy.js';

export interface SeekSubmitInput {
    input: string;
    select_model?: LLMModel;
}

export interface SeekSubmitOutput {
    seek_id: string;
}

export enum QuestionModule {
    recommend = 'RECOMMEND',
    title = 'TITLE',
    summary = 'SUMMARY',
    application = 'APPLICATION',
    reference = 'REFERENCE',
    pum_chart = 'PUM_CHART',
    to_tech_mind = 'TO_TECH_MIND',
    to_material_mind = 'TO_MATERIAL_MIND',
    default = 'DEFAULT',
    vector = 'VECTOR',
    outline = 'OUTLINE',
    other_intent_recommend = 'OTHER_INTENT_RECOMMEND',
    project_item_list = 'PROJECT_ITEM_LIST'
}

export enum QuestionModuleDataStatus {
    starting = 'starting',
    success = 'success',
    failed = 'failed',
    fetching = 'fetching',
    streaming = 'streaming'
}

export enum QuestionStatus {
    starting = 'starting',
    streaming = 'streaming',
    done = 'done'
}

export enum QuestionServerSideStatus {
    FINISH_METADATA = 'FINISH_METADATA',
    FINISH = 'FINISH',
    INIT = 'INIT'
}

export enum QuestionModuleApiType {
    http = 'http',
    sse = 'sse'
}

export enum QuestionModuleDataType {
    json = 'json',
    text = 'text',
    list = 'list'
}

export interface AgentMessage {
    module_data?: QuestionModuleData[],
    seek_id?: string,
}

export enum AgentSeekStreamMessageRenderer {
    CHECKING_SUMMARY = 'checking_summary'
}

export interface AgentStreamMessage {
    seek_id?: string,
    /**
     * 标题
     */
    title?: string,
    /**
     * 模块
     */
    modules?: QuestionModule[],
    /**
     * 总结推理过程
     */
    summary_think?: string,
    /**
     * 总结
     */
    summary?: string,
    /**
     * 检查总结
     */
    check_summary?: string,
    /**
     * 检查总结数据
     */
    check_id_to_output?: {
        [key: string]: CheckIdToOutput,
    },
    /**
     * 应用
     */
    application?: string[],
    /**
     * 引用
     */
    references?: ISolution[],
    /**
     * 状态
     */
    status?: QuestionServerSideStatus,

    renderer?: AgentSeekStreamMessageRenderer,
    /**
     * 索引签名，允许通过模块名小写形式访问内容
     */
    [key: string]: any,
}

export interface CheckIdToOutput {
    claim: string,
    explain: string,
    cites: string[],
    tag: string,
}

export interface SummaryContent {
    content: string,
    think_content: string,
    check_id_to_output?: {
        [key: string]: CheckIdToOutput,
    },
    check_summary?: string,
    renderer?: AgentSeekStreamMessageRenderer,
}

export type ReferenceContent = ISolution[]

export type QuestionModuleContent = SummaryContent & ReferenceContent & IProjectItemInfo[] & string

export interface QuestionModuleData {
    api_path: string;
    api_type: QuestionModuleApiType;
    data_type: QuestionModuleDataType;
    module_name: QuestionModule;
    priority: number;
    render: string;
    status: QuestionServerSideStatus;
    content?: QuestionModuleContent;
    _module_status?: QuestionModuleDataStatus;
    _request_signal?: string;
}

export enum QuestionIntent {
    OTHER = 'OTHER'
}

export interface QuestionData {
    seek_id: string;
    question_id: string;
    title: string;
    intent: QuestionIntent | string;
    modules: QuestionModule[];
    module_data: QuestionModuleData[];
    _question_status?: QuestionStatus;
}

export type PageInitInput = {
    seek_id: string;
    question_id?: string;
}

export type PageInitOutput = QuestionData[]

export interface PageStreamInput {
    seek_id: string;
    question_id: string;
    module: string;
    regenerate?: boolean;
    path?: string;
}

export interface OneIdInfo {
    original_address: {
        country: string;
        state: string;
        city: string;
        district: string;
        country_en: string;
        country_code: string;
    },
    website: string;
    founded_date: number;
    logo: string;
    id: string;
    display_name: string;
    description: string;
    normalized_display_name?: string;
    normalized_id?: string;
    normalized_name?: string;
}

export interface Outline {
    topic_name: string;
    children: Outline[];
}

export enum StreamStatus {
    PROCESSING = 'PROCESSING',
    FINISH = 'FINISH',
    FAILED = 'FAILED'
}

export interface StreamData {
    status: StreamStatus;
    message: QuestionModuleContent;
    post_processing?: string;
    data_type?: string;
    module_name?: string;
}

export type InitData = {
    createLoading: boolean;
    initLoading: boolean;
}

export type tippyInitType = {
    instances: Instance | null,
    clearSelectionData: () => void,
}

export enum E_ANALYSIS_CHART {
    PUM_CHART = 'PUM_CHART'
}

export enum CHART_TYPE {
    GRAPH = 'graph',
    BAR = 'bar'
}

export interface PumChartData {
    total: {
        property_gap: number[];
    },
    source: {
        property_gap: string,
        count: number,
        percentage: number
    }[],
    dimensions: string[],
    extra: {
        PROPERTY_GAP: {
            code: string,
            property: {
                property_id: string,
                property_name: string,
                unit: string,
                measurement: {
                    min_value: string,
                    max_value: string
                }
            },
            label: string
        }[],
        QUERY_PROPERTY: {
            property_id: string,
            property_name: string,
            unit: string,
            measurement: {
                min_value: string,
                max_value: string
            }
        }
    }
}

export enum CHART_KEY {
    MATERIAL_PUM = 'MATERIAL_PUM'
}

export interface PageData {
    seek_id: string;
    _init_loading: boolean;
    _page_status: QuestionStatus;
}

export interface PageModelData {
    seek_id: string;
    input: string;
    llm_model?: LLMModel;
    _request_signal: string;
    _create_loading: boolean;
    _init_loading: boolean;
    _page_status: QuestionStatus;
    _question_list: QuestionData[];
    _is_init_finished?: boolean;
    _in_agent?: boolean;
}

export interface IProjectItemInfo {
    item_id: string;
    work_project_id: string;
    work_project_name: string;
    business_id: string;
    business_node_id: string;
    business_type: string;
    title: string;
    content: string;
    content_format_type: string;
    item_process_task_id: string;
    item_process_status: string;
    created_at: string;
}

export interface IProjectItem {
    entity_id: string;
    item_info: IProjectItemInfo;
}
